import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  SecurityOutlined as SecurityIcon,
  VisibilityOff as VisibilityOffIcon 
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import axios from 'axios';

const OnboardingForm = () => {
  const { id: userId } = useParams();
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if the current user is admin or if they're viewing their own form
  const canViewForm = user?.primaryRole === 'admin' || user?.id === userId;

  const handleBack = () => {
    navigate('/admin/onboarding');
  };

  // Fetch masked user data
  const fetchMaskedUserData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/onboarding/onboarding-form-masked/${userId}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.data && response.data.data) {
        // Backend already returns masked data for security
        setFormData(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };



  // Get country flag emoji
  const getCountryFlag = (country) => {
    const flags = {
      'India': '🇮🇳',
      'Indonesia': '🇮🇩',
      'Vietnam': '🇻🇳'
    };
    return flags[country] || '🌍';
  };

  // Get document type label
  const getDocumentLabel = (country, type) => {
    const labels = {
      'India': {
        'aadhaar': 'Aadhaar Number',
        'pan': 'PAN Card Number'
      },
      'Indonesia': {
        'taxId': 'Tax Identification Number',
        'nationalId': 'National Identity Number'
      },
      'Vietnam': {
        'taxId': 'Tax Identification Number', 
        'nationalId': 'National Identity Number'
      }
    };
    return labels[country]?.[type] || type;
  };

  useEffect(() => {
    if (canViewForm && userId) {
      fetchMaskedUserData();
    }
  }, [userId, canViewForm]);

  if (!canViewForm) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          You don't have permission to view this form.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
             Onboarding Form
          </Typography>
          {user?.primaryRole === 'admin' && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Back to Onboarding Management
            </Button>
          )}
        </Box>
        {/* <Typography variant="body1" color="text.secondary">
          User ID: {userId}
        </Typography>
        {user?.primaryRole === 'admin' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Admin View - Secure masked view of employee documents
          </Typography>
        )} */}
      </Paper>

      {formData ? (
        <>
          {/* Personal Information Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 3, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              PERSONAL INFORMATION
            </Typography>
            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Employee ID</Typography>
                <Typography variant="h6">{formData.employeeId || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Name of Employee</Typography>
                <Typography variant="h6">{formData.employeeName || 'Not provided'}</Typography>
              </Grid>
              
              {/* Row 2 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Sex</Typography>
                <Typography variant="h6">{formData.sex || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Date of Joining</Typography>
                <Typography variant="h6">
                  {formData.dateOfJoining ? new Date(formData.dateOfJoining).toLocaleDateString() : 'Not provided'}
                </Typography>
              </Grid>
              
              {/* Row 3 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Country</Typography>
                <Typography variant="h6">
                  {getCountryFlag(formData.country)} {formData.country || 'Not provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Nationality</Typography>
                <Typography variant="h6">{formData.nationality || 'Not provided'}</Typography>
              </Grid>
              
              {/* Row 4 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                <Typography variant="h6">
                  {formData.dateOfBirth ? new Date(formData.dateOfBirth).toLocaleDateString() : 'Not provided'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Marital Status</Typography>
                <Typography variant="h6">{formData.maritalStatus || 'Not provided'}</Typography>
              </Grid>
              
              {/* Row 5 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Blood Group</Typography>
                <Typography variant="h6">{formData.bloodGroup || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Reporting Manager</Typography>
                <Typography variant="h6">{formData.reportingManager || 'Not provided'}</Typography>
              </Grid>
              
              {/* Row 6 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Unit Head</Typography>
                <Typography variant="h6">{formData.unitHead || 'Not provided'}</Typography>
              </Grid>

              {/* Sensitive Documents within Personal Information */}
             
            </Grid>
          </Paper>

          {/* Address Information Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Current Address
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Street</Typography>
                <Typography variant="body1">{formData.currentAddress?.street || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">City</Typography>
                <Typography variant="body1">{formData.currentAddress?.city || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {formData.country === 'India' ? 'State' : 'State/Province'}
                </Typography>
                <Typography variant="body1">{formData.currentAddress?.state || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  {formData.country === 'India' ? 'Pin-code' : 'Pin-code/ZIP Code/Postal Code'}
                </Typography>
                <Typography variant="body1">{formData.currentAddress?.zipCode || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Country</Typography>
                <Typography variant="body1">{formData.currentAddress?.country || 'Not provided'}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            {formData.permanentAddress?.sameAsCurrent ? (
              <Typography variant="body1" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                ✓ Permanent address same as current address
              </Typography>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                  Permanent Address
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Street</Typography>
                    <Typography variant="body1">{formData.permanentAddress?.street || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">City</Typography>
                    <Typography variant="body1">{formData.permanentAddress?.city || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {formData.country === 'India' ? 'State' : 'State/Province'}
                    </Typography>
                    <Typography variant="body1">{formData.permanentAddress?.state || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {formData.country === 'India' ? 'Pin-code' : 'Pin-code/ZIP Code/Postal Code'}
                    </Typography>
                    <Typography variant="body1">{formData.permanentAddress?.zipCode || 'Not provided'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">Country</Typography>
                    <Typography variant="body1">{formData.permanentAddress?.country || 'Not provided'}</Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Paper>

          {/* Contact Information & Documents Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Contact Information & Documents
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Email ID</Typography>
                <Typography variant="body1">{formData.emailId || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Mobile Number</Typography>
                <Typography variant="body1">
                  {formData.country === 'India' ? '+91' : 
                   formData.country === 'Indonesia' ? '+62' : 
                   formData.country === 'Vietnam' ? '+84' : '+XX'} {formData.mobileNumber || 'Not provided'}
                </Typography>
              </Grid>
              {(formData.country === 'India' || formData.country === 'Indonesia' || formData.country === 'Vietnam') && (
                <>
                  {/* <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SecurityIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <Typography variant="h6">
                        Secure Documents ({formData.country})
                      </Typography>
                      <Chip 
                        icon={<VisibilityOffIcon />} 
                        label="Masked for Security" 
                        color="primary" 
                        variant="outlined" 
                        sx={{ ml: 2 }}
                      />
                    </Box>
                  </Grid> */}

                  {formData.country === 'India' && (
                    <>
                      {formData.aadharNumber && (
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                🇮🇳 Aadhaar Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                                {formData.aadharNumber}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                      {formData.panCardNumber && (
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                🇮🇳 PAN Card Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 2 }}>
                                {formData.panCardNumber}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </>
                  )}

                  {(formData.country === 'Indonesia' || formData.country === 'Vietnam') && (
                    <>
                      {formData.taxIdentificationNumber && (
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {getCountryFlag(formData.country)} Tax Identification Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                                {formData.taxIdentificationNumber}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}

                      {formData.nationalIdentityNumber && (
                        <Grid item xs={12} sm={6}>
                          <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                            <CardContent>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {getCountryFlag(formData.country)} National Identity Number
                              </Typography>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                                {formData.nationalIdentityNumber}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </>
                  )}
                </>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Emergency Contact Name</Typography>
                <Typography variant="body1">{formData.emergencyContact?.name || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Emergency Contact Number</Typography>
                <Typography variant="body1">{formData.emergencyContact?.contactNo || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Relationship with Employee</Typography>
                <Typography variant="body1">{formData.emergencyContact?.relationshipWithEmployee || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Passport No.</Typography>
                <Typography variant="body1">{formData.passportNo || 'Not provided'}</Typography>
              </Grid>
            </Grid>
          </Paper>



          {/* PF Details / Social Security Details Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              PF DETAILS / Social Security Details
            </Typography>
            <Grid container spacing={3}>
              {formData.country === 'India' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">UAN No. (if applicable)</Typography>
                  <Typography variant="body1">{formData.pfDetails?.uanNumber || 'Not provided'}</Typography>
                </Grid>
              )}
              {(formData.country === 'Indonesia' || formData.country === 'Vietnam') && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Social Security No. (NSM)</Typography>
                  <Typography variant="body1">{formData.pfDetails?.socialSecurityNumber || 'Not provided'}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Work Experience Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              WORK EXPERIENCE / PROFILE
            </Typography>
            {formData.workExperiences && formData.workExperiences.length > 0 ? (
              formData.workExperiences.map((experience, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Work Experience {index + 1}
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Organization Name</Typography>
                      <Typography variant="body1">{experience.organizationName || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">Designation</Typography>
                      <Typography variant="body1">{experience.designation || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">From Date</Typography>
                      <Typography variant="body1">
                        {experience.fromDate ? new Date(experience.fromDate).toLocaleDateString() : 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">To Date</Typography>
                      <Typography variant="body1">
                        {experience.toDate ? new Date(experience.toDate).toLocaleDateString() : 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="body2" color="text.secondary">Contact Number</Typography>
                      <Typography variant="body1">{experience.contactNumber || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">Job Description</Typography>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                        {experience.jobDescription || 'Not provided'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No work experience provided
              </Typography>
            )}
          </Paper>

          {/* Family Member Details Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              FAMILY MEMBER DETAILS
            </Typography>
            {formData.familyMembers && formData.familyMembers.length > 0 ? (
              formData.familyMembers.map((member, index) => (
                <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                    Family Member {index + 1}
                  </Typography>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">Name</Typography>
                      <Typography variant="body1">{member.name || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                      <Typography variant="body1">
                        {member.dateOfBirth ? new Date(member.dateOfBirth).toLocaleDateString() : 'Not provided'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography variant="body2" color="text.secondary">Relation</Typography>
                      <Typography variant="body1">{member.relation || 'Not provided'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography variant="body2" color="text.secondary">Residing with self</Typography>
                      <Chip 
                        label={member.residingWithSelf ? 'Yes' : 'No'} 
                        color={member.residingWithSelf ? 'success' : 'default'}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No family member details provided
              </Typography>
            )}
          </Paper>

          {/* Bank Details Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              BANK DETAILS
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: 'italic', mb: 2, color: 'text.secondary' }}>
              (Please support along with a Cancelled Cheque with printed name or bank statements/passbook - front page, with details mentioned below)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Bank Name</Typography>
                <Typography variant="body1">{formData.bankDetails?.bankName || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Branch Address</Typography>
                <Typography variant="body1">{formData.bankDetails?.branchAddress || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Account Name</Typography>
                <Typography variant="body1">{formData.bankDetails?.accountName || 'Not provided'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Account No</Typography>
                <Typography variant="body1">{formData.bankDetails?.accountNo || 'Not provided'}</Typography>
              </Grid>
              {formData.country === 'India' && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">IFSC Code</Typography>
                  <Typography variant="body1">{formData.bankDetails?.ifscCode || 'Not provided'}</Typography>
                </Grid>
              )}
              {(formData.country === 'Indonesia' || formData.country === 'Vietnam') && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Swift Code/Bank Code</Typography>
                  <Typography variant="body1">{formData.bankDetails?.swiftCode || 'Not provided'}</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Declaration Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', borderBottom: '2px solid', pb: 1 }}>
              DECLARATION
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.declaration || false}
                    disabled
                    sx={{
                      mt: 0.5,
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label=""
                sx={{ minWidth: 'auto' }}
              />
              <Typography variant="body1" sx={{ lineHeight: 1.6, textAlign: 'justify' }}>
                I declare that the information given in this personal data form and the certificates accompanying it is correct and complete to best of my knowledge and belief. I also understand that at any stage I may be asked to provide adequate justification of the facts stated above, and I would do so when called for. I accept the job and position given to me in all respect and will strive to work towards company core values and help to achieve its vision and mission.
              </Typography>
            </Box>
          </Paper>


        </>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Alert severity="info">
            No form data found for this user.
          </Alert>
        </Paper>
      )}
    </Container>
  );
};

export default OnboardingForm; 